import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { enqueueSnackbar } from 'notistack';
import {
  deleteEntityDocument,
  downloadEntityDocument,
  EntityDocument,
  getEntityDocuments,
  uploadEntityDocument,
} from 'api/bitedb/entities/entityDocuments.service';

interface EntityDocumentsPanelProps {
  databaseId?: string;
  databaseEntityId?: string;
  entityName?: string;
  currentUserId?: string;
  canManageDocuments?: boolean;
  onCountChange?: (count: number) => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'png',
  'jpg',
  'jpeg',
];

const fileExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop();
  return extension ? extension.toUpperCase() : 'FILE';
};

const formatBytes = (size: number | null): string => {
  if (size === null || !Number.isFinite(size)) return 'Size unavailable';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatUploadedAt = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const fileKey = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const canDeleteEntityDocument = (
  document: EntityDocument,
  currentUserId?: string,
  canManageDocuments = false,
): boolean =>
  canManageDocuments ||
  Boolean(currentUserId && document.uploaded_by === currentUserId);

const EntityDocumentsPanel: React.FC<EntityDocumentsPanelProps> = ({
  databaseId,
  databaseEntityId,
  entityName,
  currentUserId,
  canManageDocuments = false,
  onCountChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [documents, setDocuments] = useState<EntityDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<
    string | null
  >(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canUseDocuments = Boolean(databaseId && databaseEntityId);

  useEffect(() => {
    if (!databaseId || !databaseEntityId) {
      setDocuments([]);
      onCountChange?.(0);
      return undefined;
    }
    let active = true;
    setDocuments([]);
    onCountChange?.(0);
    setIsLoading(true);
    getEntityDocuments(databaseId, databaseEntityId)
      .then((result) => {
        if (!active) return;
        setDocuments(result);
        onCountChange?.(result.length);
      })
      .catch(() => {
        if (active) {
          enqueueSnackbar('Could not load this entity’s documents.', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [databaseId, databaseEntityId, onCountChange]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!databaseId || !databaseEntityId || files.length === 0) return;
      const accepted = files.filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        if (!ACCEPTED_EXTENSIONS.includes(extension)) {
          enqueueSnackbar(`${file.name} is not a supported file type.`, {
            variant: 'warning',
          });
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          enqueueSnackbar(`${file.name} is larger than 25 MB.`, {
            variant: 'warning',
          });
          return false;
        }
        return true;
      });

      for (const file of accepted) {
        const key = fileKey(file);
        setUploadProgress((current) => ({ ...current, [key]: 0 }));
        try {
          const next = await uploadEntityDocument(
            databaseId,
            databaseEntityId,
            file,
            (progress) =>
              setUploadProgress((current) => ({
                ...current,
                [key]: progress,
              })),
          );
          setDocuments(next);
          onCountChange?.(next.length);
          enqueueSnackbar(`${file.name} uploaded.`, { variant: 'success' });
        } catch (error) {
          enqueueSnackbar(`Could not upload ${file.name}.`, {
            variant: 'error',
          });
        } finally {
          setUploadProgress((current) => {
            const next = { ...current };
            delete next[key];
            return next;
          });
        }
      }
    },
    [databaseId, databaseEntityId, onCountChange],
  );

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    uploadFiles(files);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    uploadFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleDownload = async (document: EntityDocument) => {
    if (!databaseId) return;
    try {
      await downloadEntityDocument(databaseId, document);
    } catch (error) {
      enqueueSnackbar('Could not download this document.', {
        variant: 'error',
      });
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!databaseId || deletingId) return;
    setDeletingId(documentId);
    try {
      await deleteEntityDocument(databaseId, documentId);
      setDocuments((current) => {
        const next = current.filter((document) => document.id !== documentId);
        onCountChange?.(next.length);
        return next;
      });
      setDeleteConfirmationId(null);
      enqueueSnackbar('Document removed.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Could not remove this document.', { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const uploads = Object.entries(uploadProgress);

  if (!canUseDocuments) {
    return (
      <div className='entity-hub-drawer-state'>
        Documents are unavailable because this record is not linked to a
        database entity.
      </div>
    );
  }

  return (
    <div className='entity-hub-documents'>
      <div className='entity-hub-documents-intro'>
        <div>
          <h3>Supporting documents</h3>
          <p>
            Keep evidence, certificates and review notes with{' '}
            {entityName || 'this entity'}.
          </p>
        </div>
        <span>{documents.length} attached</span>
      </div>

      <div
        className={`entity-hub-document-dropzone${
          isDragging ? ' is-dragging' : ''
        }`}
        role='button'
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setIsDragging(false);
          }
        }}
        onDrop={handleDrop}
      >
        <span className='entity-hub-document-dropzone-icon'>
          <UploadFileOutlinedIcon />
        </span>
        <div>
          <strong>Drop files here or choose files</strong>
          <p>PDF, Office, CSV, text or images · up to 25 MB each</p>
        </div>
        <button type='button' className='btn-primary' tabIndex={-1}>
          Upload
        </button>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept='.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg'
          onChange={handleFileSelection}
          aria-label='Choose documents to upload'
        />
      </div>

      <div className='entity-hub-document-process' aria-label='Upload process'>
        <span><b>1</b> Select files</span>
        <i aria-hidden='true' />
        <span><b>2</b> Store securely</span>
        <i aria-hidden='true' />
        <span><b>3</b> Share with your team</span>
      </div>

      {uploads.map(([key, progress]) => (
        <div className='entity-hub-document-uploading' key={key}>
          <div>
            <strong>{key.split('-').slice(0, -2).join('-')}</strong>
            <span>{progress}%</span>
          </div>
          <div className='entity-hub-document-progress'>
            <span style={{ width: `${progress}%` }} />
          </div>
        </div>
      ))}

      <div className='entity-hub-documents-list-header'>
        <h3>Attached files</h3>
        <span>Newest first</span>
      </div>

      {isLoading ? (
        <div className='entity-hub-document-skeleton' aria-label='Loading documents'>
          <span />
          <span />
          <span />
        </div>
      ) : documents.length === 0 ? (
        <div className='entity-hub-documents-empty'>
          <DescriptionOutlinedIcon />
          <strong>No documents attached yet</strong>
          <p>Upload the first file to keep the review evidence in one place.</p>
        </div>
      ) : (
        <div className='entity-hub-document-list'>
          {documents.map((document) => (
            <div className='entity-hub-document-row' key={document.id}>
              <span className='entity-hub-document-file-icon'>
                <DescriptionOutlinedIcon />
                <small>{fileExtension(document.file_name)}</small>
              </span>
              <div className='entity-hub-document-file-copy'>
                <button
                  type='button'
                  onClick={() => handleDownload(document)}
                  title={document.file_name}
                >
                  {document.file_name}
                </button>
                <p>
                  {formatBytes(document.file_size)} ·{' '}
                  {formatUploadedAt(document.created_at)}
                  {document.uploaded_by_name
                    ? ` · ${document.uploaded_by_name}`
                    : ''}
                </p>
              </div>
              <span className='entity-hub-document-ready' title='Ready'>
                <CheckCircleOutlineOutlinedIcon />
              </span>
              {canDeleteEntityDocument(
                document,
                currentUserId,
                canManageDocuments,
              ) && deleteConfirmationId === document.id ? (
                <div className='entity-hub-document-delete-confirm'>
                  <button
                    type='button'
                    onClick={() => setDeleteConfirmationId(null)}
                    disabled={deletingId === document.id}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='danger'
                    onClick={() => handleDelete(document.id)}
                    disabled={deletingId === document.id}
                  >
                    {deletingId === document.id ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              ) : (
                <div className='entity-hub-document-row-actions'>
                  <button
                    type='button'
                    aria-label={`Download ${document.file_name}`}
                    onClick={() => handleDownload(document)}
                  >
                    <DownloadOutlinedIcon />
                  </button>
                  {canDeleteEntityDocument(
                    document,
                    currentUserId,
                    canManageDocuments,
                  ) ? (
                    <button
                      type='button'
                      aria-label={`Remove ${document.file_name}`}
                      onClick={() => setDeleteConfirmationId(document.id)}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EntityDocumentsPanel;
